import { Suspense } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { PageLoader } from '@/components/ui';
import { useUser } from '@/lib/auth';
import { ADMIN_NAV, isNavActive, pageTitle } from './nav';

/**
 * The administration shell.
 *
 * Deliberately not a re-skin of `AppLayout`: the chrome sits on `--color-surface-2`
 * with a stronger border and a kerb strip under the header, there is a permanent
 * "modo administrador" marker, and the way back to the normal app is the first
 * thing in the sidebar. The distinctness comes from surface treatment rather than
 * a new accent hue, because every accent colour in this design system already
 * means something specific on the telemetry screens.
 */
export function AdminLayout() {
  const location = useLocation();
  const user = useUser();
  const title = pageTitle(location.pathname);

  return (
    <div className="min-h-dvh bg-bg">
      <a
        href="#conteudo-admin"
        className={cn(
          'sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50',
          'focus:inline-flex focus:min-h-11 focus:items-center focus:rounded-md focus:border focus:border-border-strong',
          'focus:bg-surface-2 focus:px-4 focus:text-base',
        )}
      >
        Pular para o conteúdo
      </a>

      {/* --- Sidebar (lg and up) --------------------------------------- */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden w-[var(--width-sidebar)] flex-col',
          'border-r-2 border-border-strong bg-surface-2 pt-[env(safe-area-inset-top)] lg:flex',
        )}
      >
        <div className="relative flex h-14 shrink-0 items-center gap-2 overflow-hidden px-4">
          <span aria-hidden="true" className="kerb absolute inset-x-0 bottom-0 h-1" />
          <ShieldAlert aria-hidden="true" className="size-5 shrink-0 text-primary" />
          <span className="font-display text-lg font-extrabold uppercase tracking-wide text-text">
            Administração
          </span>
        </div>

        <nav aria-label="Navegação administrativa" className="flex-1 overflow-y-auto px-2 py-2">
          <ul className="flex flex-col gap-1">
            {ADMIN_NAV.map((item) => {
              const active = isNavActive(item, location.pathname);
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'relative flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-base font-medium',
                      'transition-colors duration-150 ease-out-soft',
                      active ? 'bg-primary-dim text-text' : 'text-text-muted hover:bg-surface-3 hover:text-text',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute inset-y-1.5 left-0 w-0.5 rounded-full',
                        active ? 'bg-primary' : 'bg-transparent',
                      )}
                    />
                    <Icon aria-hidden="true" className="size-5 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="shrink-0 border-t border-border p-2">
          <p className="px-3 pb-1 text-2xs text-text-faint">
            Conectado como <span className="text-text-muted">{user?.nickname ?? '—'}</span>
          </p>
          <Link
            to="/"
            className={cn(
              'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-base font-medium',
              'text-text-muted transition-colors duration-150 ease-out-soft hover:bg-surface-3 hover:text-text',
            )}
          >
            <ArrowLeft aria-hidden="true" className="size-5 shrink-0" />
            <span>Voltar ao app</span>
          </Link>
        </div>
      </aside>

      {/* --- Top bar ---------------------------------------------------- */}
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-30 border-b-2 border-border-strong bg-surface-2/95 backdrop-blur',
          'pt-[env(safe-area-inset-top)] lg:left-[var(--width-sidebar)]',
        )}
      >
        <div className="flex h-[var(--height-header)] items-center gap-2 px-gutter lg:h-12">
          <Link
            to="/"
            aria-label="Voltar ao app"
            title="Voltar ao app"
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-md text-text-muted lg:hidden',
              'transition-colors duration-150 ease-out-soft hover:bg-surface-3 hover:text-text',
            )}
          >
            <ArrowLeft aria-hidden="true" className="size-5" />
          </Link>

          <h1 className="min-w-0 flex-1 truncate text-lg text-text lg:text-xl">{title}</h1>

          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-primary/50 bg-primary-dim',
              'px-2 py-1 text-2xs font-bold uppercase tracking-wider text-primary',
            )}
          >
            <ShieldAlert aria-hidden="true" className="size-3.5" />
            <span className="hidden sm:inline">Modo administrador</span>
            <span className="sm:hidden">Admin</span>
          </span>
        </div>
        <div aria-hidden="true" className="kerb h-1" />
      </header>

      {/* --- Mobile section nav ----------------------------------------- */}
      <nav
        aria-label="Seções da administração"
        className={cn(
          'scroll-x fixed inset-x-0 z-20 border-b border-border bg-surface lg:hidden',
          'top-[calc(var(--height-header)+env(safe-area-inset-top)+4px)]',
        )}
      >
        <ul className="flex items-stretch gap-1 px-2">
          {ADMIN_NAV.map((item) => {
            const active = isNavActive(item, location.pathname);
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-11 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 text-sm font-semibold',
                    'transition-colors duration-150 ease-out-soft',
                    active ? 'border-primary text-text' : 'border-transparent text-text-muted',
                  )}
                >
                  <Icon aria-hidden="true" className="size-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* --- Content ---------------------------------------------------- */}
      <div className="lg:pl-[var(--width-sidebar)]">
        <main
          id="conteudo-admin"
          className={cn(
            'safe-bottom mx-auto w-full max-w-[1400px] px-gutter',
            'pt-[calc(var(--height-header)+env(safe-area-inset-top)+3.75rem)]',
            'lg:pt-[calc(3rem+env(safe-area-inset-top)+1.75rem)]',
          )}
        >
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
